/** Which wage a shift went out with, for analytics: the picker's choice, told apart by what the wage equals. */
export type WageChoice = "recommended" | "saved" | "typed" | "avg";

type WageChoiceInput = {
  /** The picker's option; the posting page keeps none, so there it is left out and the wage tells. */
  key?: "recommended" | "avg" | "custom";
  wage: number;
  recommendedWage?: number | null;
  /** The saved position's wage: what the custom option starts at. */
  savedWage?: number | null;
};

export const wageChoiceOf = ({ key, wage, recommendedWage, savedWage }: WageChoiceInput): WageChoice => {
  if (key === "avg") return "avg";
  if (key === "recommended") return "recommended";
  // The page's picker shows Recommended as picked whenever the wage equals it, so there it is the choice.
  if (key === undefined && recommendedWage != null && wage === recommendedWage) return "recommended";
  return savedWage != null && wage === savedWage ? "saved" : "typed";
};

export type WagePosition = {
  skill_id: number;
  wage: number;
  recommended_wage: number | null;
  saved_wage: number | null;
  wage_choice: WageChoice;
};

type PositionInput = Omit<WagePosition, "wage_choice"> & Pick<WageChoiceInput, "key">;

export const wagePositionOf = ({ key, ...position }: PositionInput): WagePosition => ({
  ...position,
  wage_choice: wageChoiceOf({
    key,
    wage: position.wage,
    recommendedWage: position.recommended_wage,
    savedWage: position.saved_wage,
  }),
});

const CHOICES: ReadonlyArray<WageChoice> = ["recommended", "saved", "typed", "avg"];

/** A post's positions as event props: one position flat, several as an array, with a count per choice either way. */
export const wageChoiceProps = (positions: ReadonlyArray<WagePosition>) => {
  const counts = Object.fromEntries(
    CHOICES.map((choice) => [
      `wage_choice_${choice}_count`,
      positions.filter(({ wage_choice }) => wage_choice === choice).length,
    ]),
  );
  const [only] = positions;
  return only && positions.length === 1
    ? { ...only, ...counts }
    : { positions, position_count: positions.length, ...counts };
};

type OptionInput = {
  option: "recommended" | "avg" | "custom";
  wage: number;
  recommendedWage: number;
  savedWage?: number | null;
};

/** The click event's props; the picker's own default selection never reaches it. */
export const wageOptionProps = ({ option, wage, recommendedWage, savedWage }: OptionInput) => ({
  type: "wage_picker",
  option: wageChoiceOf({ key: option, wage, savedWage }),
  wage,
  recommended_wage: recommendedWage,
  saved_wage: savedWage,
});

type PagePosition = {
  position_id?: string | null;
  template_id?: string | null;
  pay_rate?: number | null;
  last_wage?: number | null;
};

type PageLookups = {
  /** `wage-recommendation-refresh`: Recommended is the market's; without it, last time's wage when the role has one. */
  refresh: boolean;
  skillRecommended: (positionId: string) => number | null | undefined;
  savedWage: (templateId: string) => number | null | undefined;
};

/** The roles a posting-page post went out with; a role with no pay (never posted) is left out. */
export const pageWagePositions = (
  shifts: ReadonlyArray<{ positions: ReadonlyArray<PagePosition> }>,
  { refresh, skillRecommended, savedWage }: PageLookups,
): Array<WagePosition> =>
  shifts
    .flatMap(({ positions }) => positions)
    .flatMap((position) => {
      if (!position.position_id || !position.pay_rate) return [];
      const market = skillRecommended(position.position_id);
      return [
        wagePositionOf({
          skill_id: Number(position.position_id),
          wage: position.pay_rate,
          recommended_wage: (refresh ? market : (position.last_wage ?? market)) ?? null,
          saved_wage: (position.template_id ? savedWage(position.template_id) : null) ?? null,
        }),
      ];
    });

type ClassicEntry = {
  pro: { skill_id?: number | null; hourly_rate?: number | null };
  count: number;
  wageOverride?: {
    key: "recommended" | "avg" | "custom";
    wage: number;
    skill: { recommended_wage_amount?: number | null };
  };
};

/** The roles the classic form posts: the picker's own option rides on each, and the saved wage is the position's. */
export const classicWagePositions = (entries: ReadonlyArray<ClassicEntry>): Array<WagePosition> =>
  entries.flatMap(({ pro, count, wageOverride }) => {
    const wage = wageOverride?.wage ?? pro.hourly_rate;
    if (count === 0 || !wage || pro.skill_id == null) return [];
    return [
      wagePositionOf({
        skill_id: pro.skill_id,
        // No pick was made: the saved wage the picker started at went out.
        key: wageOverride?.key ?? "custom",
        wage,
        recommended_wage: wageOverride?.skill.recommended_wage_amount ?? null,
        saved_wage: pro.hourly_rate ?? null,
      }),
    ];
  });
